import React, { useReducer, useCallback } from 'react';
import { TFileId, TLanguageIDs, IItemForGetShortInfo, IDefaultLanguage, ICreateSubcategoryV2 } from 'types';
import {
	createSubcategoryInitialState,
	CreateSubcategoryContext,
	TTypeOfModalWindow,
} from './createSubcategoryContext';
import { createSubcategoryReducer } from './createSubcategoryReducer';
import { CreateSubcategoryActionTypes } from './createSubcategoryActions';

export const CreateSubcategoryState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(createSubcategoryReducer, createSubcategoryInitialState);

	const setFileId = useCallback((value: TFileId) => {
		dispatch({
			type: CreateSubcategoryActionTypes.SET_FILE_ID,
			payload: {
				fileId: value,
			},
		});
	}, []);

	const setFetchedCustomers = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateSubcategoryActionTypes.SET_FETCHED_CUSTOMERS,
			payload: {
				fetchedCustomers: value,
			},
		});
	}, []);

	const setIDCustomer = useCallback((value: Nullable<string>) => {
		dispatch({
			type: CreateSubcategoryActionTypes.SET_ID_CUSTOMER,
			payload: {
				IDSelectedCustomer: value,
			},
		});
	}, []);

	const setFetchedRestaurants = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateSubcategoryActionTypes.SET_FETCHED_RESTAURANTS,
			payload: {
				fetchedRestaurants: value,
			},
		});
	}, []);

	const setIDsRestaurant = useCallback((value: string[]) => {
		dispatch({
			type: CreateSubcategoryActionTypes.SET_IDS_RESTAURANT,
			payload: {
				IDsSelectedRestaurant: value,
			},
		});
	}, []);

	const setFetchedCategories = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateSubcategoryActionTypes.SET_FETCHED_CATEGORIES,
			payload: {
				fetchedCategories: value,
			},
		});
	}, []);

	const setIDCategory = useCallback((value: Nullable<string>) => {
		dispatch({
			type: CreateSubcategoryActionTypes.SET_ID_CATEGORY,
			payload: {
				IDSelectedCategory: value,
			},
		});
	}, []);

	const changeNameOfSubcategory = useCallback((value: string) => {
		dispatch({
			type: CreateSubcategoryActionTypes.CHANGE_NAME_OF_SUBCATEGORY,
			payload: {
				nameOfSubcategory: value,
			},
		});
	}, []);

	const toggleHasSchedulePriority = useCallback(() => {
		dispatch({
			type: CreateSubcategoryActionTypes.TOGGLE_HAS_SCHEDULE_PRIORITY,
		});
	}, []);

	const changeActivationSchedule = useCallback((value: string) => {
		dispatch({
			type: CreateSubcategoryActionTypes.CHANGE_ACTIVATION_SCHEDULE,
			payload: {
				activationSchedule: value,
			},
		});
	}, []);

	const changeDeactivationSchedule = useCallback((value: string) => {
		dispatch({
			type: CreateSubcategoryActionTypes.CHANGE_DEACTIVATION_SCHEDULE,
			payload: {
				deactivationSchedule: value,
			},
		});
	}, []);

	const setFetchedTranslations = useCallback(
		(value: TLanguageIDs[], defaultCustomerLanguage: Nullable<IDefaultLanguage>) => {
			const translations = value.map((id) => ({ languageId: id, name: '' }));
			dispatch({
				type: CreateSubcategoryActionTypes.SET_FETCHED_TRANSLATIONS,
				payload: {
					translations,
					defaultCustomerLanguage,
				},
			});
		},
		[],
	);

	const setTranslationTab = useCallback((value: Nullable<TLanguageIDs>) => {
		dispatch({
			type: CreateSubcategoryActionTypes.SET_TRANSLATION_TAB,
			payload: {
				selectedTranslationTab: value,
			},
		});
	}, []);

	const changeNameTranslation = useCallback(
		(languageId: TLanguageIDs, value: string) => {
			const updatedTranslations = state.translations.map((translation) =>
				translation.languageId === languageId
					? {
							...translation,
							name: value,
					  }
					: translation,
			);
			dispatch({
				type: CreateSubcategoryActionTypes.CHANGE_TRANSLATION,
				payload: {
					translations: updatedTranslations,
				},
			});
		},
		[state.translations],
	);

	const setDataForSubmit = useCallback((value: Nullable<ICreateSubcategoryV2>) => {
		dispatch({
			type: CreateSubcategoryActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: CreateSubcategoryActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const resetFields = useCallback(() => dispatch({ type: CreateSubcategoryActionTypes.RESET_FIELDS }), []);

	return (
		<CreateSubcategoryContext.Provider
			value={{
				setFileId,
				setFetchedCustomers,
				setIDCustomer,
				setFetchedRestaurants,
				setIDsRestaurant,
				setFetchedCategories,
				setIDCategory,
				changeNameOfSubcategory,
				toggleHasSchedulePriority,
				changeActivationSchedule,
				changeDeactivationSchedule,
				setFetchedTranslations,
				setTranslationTab,
				changeNameTranslation,
				setDataForSubmit,
				changeTypeOfModalWindow,
				resetFields,
				fileId: state.fileId,
				fetchedCustomers: state.fetchedCustomers,
				IDSelectedCustomer: state.IDSelectedCustomer,
				fetchedRestaurants: state.fetchedRestaurants,
				IDsSelectedRestaurant: state.IDsSelectedRestaurant,
				fetchedCategories: state.fetchedCategories,
				IDSelectedCategory: state.IDSelectedCategory,
				nameOfSubcategory: state.nameOfSubcategory,
				hasSchedulePriority: state.hasSchedulePriority,
				activationSchedule: state.activationSchedule,
				deactivationSchedule: state.deactivationSchedule,
				defaultCustomerLanguage: state.defaultCustomerLanguage,
				selectedTranslationTab: state.selectedTranslationTab,
				translations: state.translations,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</CreateSubcategoryContext.Provider>
	);
};
